import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { TargetBox } from 'components/DragDrop'
import { useRef, useState } from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'
import { toast } from 'react-toastify'
import { type OriginatorLockAiExtractData, extractOriginatorLockFromPdf, uploadFiles } from 'services'
import { Button, Modal } from 'stories/components'
import { formatDate } from 'utils'

type Props = {
  isOpen: boolean
  loanNumber: number
  onClose: () => void
  onApply: (result: OriginatorLockAiExtractData) => void
}

const isAcceptedLockDoc = (name: string) => {
  const lower = (name || '').toLowerCase()
  return lower.endsWith('.pdf') || /\.(png|jpe?g|webp|gif|tiff?)$/i.test(lower)
}

const PREVIEW_FIELDS: Array<{
  key: keyof OriginatorLockAiExtractData
  label: string
  date?: boolean
  boolean?: boolean
}> = [
  { key: 'LockDays', label: 'Lock Days' },
  { key: 'LockFloating', label: 'Floating', boolean: true },
  { key: 'UnextendedLockExpDate', label: 'Original Lock Exp Date', date: true },
  { key: 'LockExtension1Days', label: 'Lock Ext1' },
  { key: 'LockExtension2Days', label: 'Lock Ext2' },
  { key: 'LockExtension3Days', label: 'Lock Ext3' },
  { key: 'BuyCommitmentNo', label: 'Originator Commitment No' },
  { key: 'LockStartDate', label: 'Date Locked', date: true },
  { key: 'LockExpirationDate', label: 'Lock Expiration Date', date: true },
  { key: 'LockCanceledDate', label: 'Date Cancelled', date: true },
  { key: 'RegulatoryLockDateOV', label: 'Regulatory Lock Date (Override)', date: true },
]

const formatPreviewValue = (
  value: OriginatorLockAiExtractData[keyof OriginatorLockAiExtractData],
  field: (typeof PREVIEW_FIELDS)[number],
) => {
  if (value == null || value === '') return '—'
  if (typeof value === 'object') return '—'
  if (field.boolean) return value ? 'Yes' : 'No'
  if (field.date) return formatDate(String(value))
  return String(value)
}

export const OriginatorLockAiExtractModal = ({ isOpen, loanNumber, onClose, onApply }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [result, setResult] = useState<OriginatorLockAiExtractData | null>(null)

  const reset = () => {
    setLoading(false)
    setFileName('')
    setResult(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const runExtractFromFileKey = async (fileKey: string, name: string) => {
    setLoading(true)
    setResult(null)
    setFileName(name)
    try {
      const res = await extractOriginatorLockFromPdf([{ fileKey: String(fileKey), name }])
      if (res?.success && res.data) {
        setResult(res.data)
        const hasFields = PREVIEW_FIELDS.some(({ key }) => {
          const v = res.data[key]
          return v !== null && v !== undefined && v !== ''
        })
        if (!hasFields) toast('No lock details were found in the document.', { type: 'warning' })
      } else {
        toast(res?.message || 'Failed to extract lock details from the document.', { type: 'error' })
      }
    } catch {
      toast('Failed to extract lock details from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const runExtract = async (file: File) => {
    if (!isAcceptedLockDoc(file.name)) {
      toast('Please use a PDF or image lock sheet.', { type: 'error' })
      return
    }
    setLoading(true)
    setResult(null)
    setFileName(file.name)
    try {
      const keys: string[] = await uploadFiles(
        {
          path: `loan/${loanNumber}/secondary-marketing-ai`,
        },
        [file],
      )
      const fileKey = keys?.[0]
      if (!fileKey) {
        toast('Failed to upload lock sheet.', { type: 'error' })
        return
      }

      const res = await extractOriginatorLockFromPdf([{ fileKey: String(fileKey), name: file.name }])
      if (res?.success && res.data) {
        setResult(res.data)
        const hasFields = PREVIEW_FIELDS.some(({ key }) => {
          const v = res.data[key]
          return v !== null && v !== undefined && v !== ''
        })
        if (!hasFields) toast('No lock details were found in the document.', { type: 'warning' })
      } else {
        toast(res?.message || 'Failed to extract lock details from the document.', { type: 'error' })
      }
    } catch {
      toast('Failed to extract lock details from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onFilePicked = (files: FileList | File[] | null) => {
    const file = files?.[0]
    if (!file) return
    runExtract(file)
  }

  const onDropItem = (item: any) => {
    if (item?.files?.length) {
      onFilePicked(item.files)
      return
    }

    const docs: any[] = Array.isArray(item?.documents) ? item.documents : [item]
    const doc = docs.find((d) => d?.fileKey || d?.key) || docs[0]
    const fileKey = doc?.fileKey || doc?.key
    const name = doc?.name || 'document.pdf'
    if (!fileKey) {
      toast('Could not read the dropped document.', { type: 'error' })
      return
    }
    if (!isAcceptedLockDoc(name)) {
      toast('Please drop a PDF or image lock sheet.', { type: 'error' })
      return
    }
    runExtractFromFileKey(String(fileKey), name)
  }

  const canApply =
    !!result &&
    PREVIEW_FIELDS.some(({ key }) => {
      const v = result[key]
      return v !== null && v !== undefined && v !== ''
    })

  return (
    <Modal
      title="Extract Lock Info from PDF"
      isOpen={isOpen}
      onClose={handleClose}
      titleOkay={canApply ? 'Apply Extracted Fields' : ''}
      onOk={() => {
        if (!canApply || !result) return
        onApply(result)
        handleClose()
      }}
      loading={loading}
      disabled={!canApply}
    >
      <div className="w-[720px] max-w-full space-y-4">
        <p className="text-sm text-gray-600">
          Drop a rate lock confirmation, lock sheet, or commitment PDF/image (from your computer or the Documents
          floater). AI will extract Originator Lock Info. Review the values, then apply — Save Changes still commits
          them to the loan.
        </p>

        <TargetBox
          types={['document', NativeTypes.FILE]}
          onDrop={onDropItem}
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors border-gray-300 hover:border-shade-blue"
          highlightClassName="border-shade-blue bg-blue-50"
        >
          <div onClick={() => inputRef.current?.click()}>
            <DocumentArrowUpIcon className="w-10 h-10 mx-auto text-shade-blue mb-2" />
            <div className="font-medium text-gray-800">Drop lock sheet PDF or image here</div>
            <div className="text-sm text-gray-500 mt-1">or click to browse</div>
            {fileName && <div className="text-xs text-gray-500 mt-2">Selected: {fileName}</div>}
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf,image/*"
              className="hidden"
              onChange={(e) => onFilePicked(e.target.files)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </TargetBox>

        {loading && <div className="text-sm text-shade-blue font-medium">AI is reading the lock sheet…</div>}

        {result && (
          <div className="space-y-4">
            <div>
              <div className="font-semibold mb-2">Extracted fields</div>
              <div className="overflow-auto border rounded max-h-72">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="p-2">Field</th>
                      <th className="p-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PREVIEW_FIELDS.map((field) => (
                      <tr key={field.key} className="border-t">
                        <td className="p-2 text-gray-600">{field.label}</td>
                        <td className="p-2">{formatPreviewValue(result[field.key], field)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                link
                color="gray"
                onClick={() => {
                  reset()
                }}
              >
                Choose another file
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
